import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiErrorInfo } from "@/lib/api";

// Only real API data is ever returned. There is deliberately no mock/fixture
// fallback: an empty answer is shown as empty (ALL-5707).
export type DataSource = "api";

export interface UseApiQueryResult<T> {
  data: T;
  loading: boolean;
  error: ApiErrorInfo | null;
  source: DataSource;
  refresh: () => void;
}

export interface UseApiQueryOptions<T> {
  initialData: T;
  enabled?: boolean;
}

/**
 * Generic hook for API data fetching. Replaces the repeated
 * useState(loading) + useState(error) + useState(data) + useCallback + useEffect
 * pattern used across every feature hook.
 *
 * Usage:
 *   const { data: wallets, loading, error, source, refresh } = useApiQuery(
 *     () => fetchWallets(),
 *     { initialData: [] }
 *   );
 */
export function useApiQuery<TRaw, T>(
  fetcher: (signal: AbortSignal) => Promise<TRaw>,
  mapper: (raw: TRaw) => { data: T; error?: ApiErrorInfo; isEmpty?: boolean },
  options: UseApiQueryOptions<T>,
): UseApiQueryResult<T> {
  const [data, setData] = useState<T>(options.initialData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiErrorInfo | null>(null);
  const [source, setSource] = useState<DataSource>("api");
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    if (options.enabled === false) {
      abortRef.current?.abort();
      setData(options.initialData);
      setLoading(false);
      setError(null);
      setSource("api");
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const raw = await fetcher(controller.signal);
      if (controller.signal.aborted) return;

      const result = mapper(raw);
      if (result.error) {
        setData(options.initialData);
        setSource("api");
        setError(result.error);
      } else {
        setData(result.data);
        setSource("api");
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setData(options.initialData);
      setSource("api");
      setError({
        reasonCode: err instanceof Error ? err.message : "NETWORK_ERROR",
      });
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [fetcher, mapper, options.enabled, options.initialData]);

  useEffect(() => {
    load();
    return () => { abortRef.current?.abort(); };
  }, [load]);

  return { data, loading, error, source, refresh: load };
}

// Module-level so its identity is stable: `useApiQuery` re-runs its effect
// whenever `initialData` changes by identity, and an inline `[]` here is a new
// array on every render.
const EMPTY_LIST: never[] = [];

/**
 * Simplified variant for fetchers that return the final typed data directly.
 * No raw-to-domain mapping needed — use when the API function already returns
 * domain types (e.g., fetchAdminRoles already returns AdminRoleDefinition[]).
 */
export function useApiList<T>(
  fetcher: (signal: AbortSignal) => Promise<{ items: T[]; error?: string }>,
): UseApiQueryResult<T[]> {
  // `mapper` and `initialData` are dependencies of the effect that runs the
  // fetch, so both must keep a stable identity. A mapper lambda written inline
  // here (or an inline `[]` for initialData) changes on every render, which
  // makes `load` change, which re-runs the effect, which re-renders — an
  // unbounded refetch loop. `useCallback`/`EMPTY_LIST` keep them constant.
  const mapList = useCallback(
    (raw: { items: T[]; error?: string }) => ({
      data: raw.items,
      error: raw.error ? { reasonCode: raw.error } : undefined,
      isEmpty: raw.items.length === 0,
    }),
    [],
  );

  return useApiQuery<{ items: T[]; error?: string }, T[]>(fetcher, mapList, {
    initialData: EMPTY_LIST,
  });
}
